import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { MessageCircle, Minus, Plus, Trash2, Send } from "lucide-react";
import { useStore, API } from "@/context/StoreContext";
import { waLink, buildOrderMessage } from "@/lib/whatsapp";
import { formatGs } from "@/lib/format";

const inputCls = "w-full h-12 px-4 rounded-xl border-2 border-ink bg-white font-medium focus:outline-none focus:ring-2 focus:ring-kpink/60 placeholder:text-ink/40";

export default function OrderForm() {
  const { cart, updateQty, removeFromCart, clearCart, cartTotal } = useStore();
  const [form, setForm] = useState({ name: "", phone: "", city: "", delivery: "Retiro en el local", notes: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Completá tu nombre y teléfono para enviar el pedido");
      return;
    }
    if (cart.length === 0 && !form.notes.trim()) {
      toast.error("Agregá productos o escribí lo que necesitás en las notas");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/orders`, {
        ...form,
        items: cart.map((i) => ({ name: i.name, qty: i.qty, price: i.price })),
        total: cartTotal,
      });
    } catch (err) {
      console.error(err);
    }
    const msg = buildOrderMessage({ ...form, cart, total: cartTotal });
    window.open(waLink(msg), "_blank");
    toast.success("¡Pedido listo! Se abrió WhatsApp con tu mensaje");
    clearCart();
    setForm({ name: "", phone: "", city: "", delivery: "Retiro en el local", notes: "" });
    setSending(false);
  };

  return (
    <section id="pedido" className="py-20 lg:py-28 bg-paper border-b-2 border-ink grain relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.6 }} className="text-center">
          <span className="font-mono text-xs uppercase tracking-[0.2em] font-bold text-kcyan">Armá tu pedido</span>
          <h2 className="font-display font-extrabold tracking-tight text-2xl sm:text-3xl lg:text-4xl mt-2">
            Tu pedido, directo a nuestro WhatsApp
          </h2>
          <p className="text-ink/60 mt-3 max-w-xl mx-auto font-medium">
            Completá tus datos, revisá los productos y enviá todo con un solo toque.
          </p>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.7, delay: 0.1 }}
          className="mt-12 grid lg:grid-cols-2 gap-8 max-w-5xl mx-auto">

          <div className="bg-white border-2 border-ink rounded-3xl p-6 shadow-neo">
            <h3 className="font-display font-bold text-xl mb-4">Tu selección</h3>
            {cart.length === 0 ? (
              <p className="text-ink/50 font-medium py-8 text-center" data-testid="order-cart-empty">
                Todavía no agregaste productos. Explorá el catálogo y tocá “Agregar”.
              </p>
            ) : (
              <ul className="space-y-3" data-testid="order-cart-list">
                {cart.map((i) => (
                  <li key={i.id} className="flex items-center gap-3 border-2 border-ink rounded-2xl p-3 bg-cream">
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-sm truncate">{i.name}</p>
                      <p className="text-xs text-ink/60 font-semibold">{formatGs(i.price)} c/u</p>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <button onClick={() => updateQty(i.id, i.qty - 1)} data-testid={`cart-qty-minus-${i.id}`}
                        className="h-8 w-8 grid place-items-center rounded-lg border-2 border-ink bg-white hover:bg-kyellow transition-colors">
                        <Minus size={14} strokeWidth={3} />
                      </button>
                      <span className="w-7 text-center font-bold text-sm">{i.qty}</span>
                      <button onClick={() => updateQty(i.id, i.qty + 1)} data-testid={`cart-qty-plus-${i.id}`}
                        className="h-8 w-8 grid place-items-center rounded-lg border-2 border-ink bg-white hover:bg-kyellow transition-colors">
                        <Plus size={14} strokeWidth={3} />
                      </button>
                      <button onClick={() => removeFromCart(i.id)} data-testid={`cart-remove-${i.id}`}
                        className="h-8 w-8 grid place-items-center rounded-lg border-2 border-ink bg-white hover:bg-kpink hover:text-white transition-colors">
                        <Trash2 size={14} strokeWidth={2.5} />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
            <div className="flex items-center justify-between mt-5 pt-4 border-t-2 border-dashed border-ink/30">
              <span className="font-bold text-ink/70">Total estimado</span>
              <span className="font-display font-extrabold text-2xl" data-testid="order-cart-total">{formatGs(cartTotal)}</span>
            </div>
            <p className="text-xs text-ink/50 mt-2 font-medium">
              Recordá: para apartar o personalizar se requiere la seña del 50%. El pago total se realiza antes del retiro o despacho.
            </p>
          </div>

          <form onSubmit={submit} className="bg-white border-2 border-ink rounded-3xl p-6 shadow-neo-pink space-y-4" data-testid="order-form">
            <h3 className="font-display font-bold text-xl">Tus datos</h3>
            <input value={form.name} onChange={set("name")} placeholder="Tu nombre *" className={inputCls} data-testid="order-form-name-input" />
            <input value={form.phone} onChange={set("phone")} placeholder="Tu teléfono *" className={inputCls} data-testid="order-form-phone-input" />
            <input value={form.city} onChange={set("city")} placeholder="Ciudad (ej: Luque)" className={inputCls} data-testid="order-form-city-input" />
            <select value={form.delivery} onChange={set("delivery")} className={inputCls} data-testid="order-form-delivery-select">
              <option>Retiro en el local</option>
              <option>Envío a domicilio (costo adicional)</option>
            </select>
            <textarea value={form.notes} onChange={set("notes")} rows={3}
              placeholder="¿Buscás algo que no está en el catálogo? Contanos acá..."
              className="w-full px-4 py-3 rounded-xl border-2 border-ink bg-white font-medium focus:outline-none focus:ring-2 focus:ring-kpink/60 placeholder:text-ink/40 resize-none"
              data-testid="order-form-notes-input" />
            <button type="submit" disabled={sending} data-testid="order-form-submit-whatsapp-btn"
              className="w-full h-14 inline-flex items-center justify-center gap-2 rounded-2xl border-2 border-ink bg-[#25D366] text-white font-display font-bold text-lg shadow-neo hover:shadow-neo-lg hover:-translate-y-1 active:translate-y-0 transition-all duration-200 disabled:opacity-60">
              {sending ? "Enviando..." : (<>Enviar pedido por WhatsApp <Send size={20} strokeWidth={2.5} /></>)}
            </button>
          </form>
        </motion.div>
      </div>
    </section>
  );
}
